export type AnsweredBy = 'human' | 'voicemail' | 'no answer' | null | undefined;
export type CallOutcome =
  | 'follow_up'
  | 'conversation'
  | 'not_interest'
  | 'do_not_call'
  | 'unclear'
  | 'nurture'
  | 'incomplete'
  | string;

export type FlowType = 'Engage' | 'Convert' | 'Revive' | string;
export type IDXType = 'View' | 'Favorite' | 'Search';
export type IntentType = 'Buy' | 'Sell' | 'Buyer' | 'Seller' | string;
export type LeadSourceType = 'Email' | 'PPC' | 'Organic' | string;
export type WebActivityEventType =
  | 'page_view'
  | 'click'
  | 'search'
  | 'property_view'
  | 'property_saved'
  | 'lead_capture'
  | 'form_submission'
  | string;

interface BadgeStyle {
  background: string;
  text: string;
}

export const getAnsweredByStyle = (answeredBy: AnsweredBy): BadgeStyle | null => {
  switch (answeredBy) {
    case 'human':
      return { background: 'badge-purple', text: '' };
    case 'voicemail':
      return { background: 'badge-orange', text: '' };
    case 'no answer':
      return { background: 'badge-red', text: '' };
    default:
      return null;
  }
};

export const getCallOutcomeStyle = (outcome: CallOutcome | null | undefined): BadgeStyle => {
  switch ((outcome ?? '').toLowerCase()) {
    case 'follow_up':
    case 'active':
    case 'inbound':
      return { background: 'badge-green', text: '' };
    case 'conversation':
    case 'alert':
    case 'valid':
      return { background: 'badge-yellow', text: '' };
    case 'not_interested':
    case 'do_not_call':
    case 'no_contact':
    case 'no_answer':
    case 'no answer':
      return { background: 'badge-red', text: '' };
    // SPEC-1888: unreachable / carrier-failure — distinct from no_answer.
    case 'incomplete':
      return { background: 'badge-gray', text: '' };
    case 'voicemail':
      return { background: 'badge-orange', text: '' };
    case 'unclear':
      return { background: 'badge-orange', text: '' };
    case 'nurture':
      return { background: 'badge-blue', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

// Canonical readable label per outcome. Aliases (not_interest/not_interested)
// collapse to one label so the UI reads consistently.
const OUTCOME_LABELS: Record<string, string> = {
  follow_up: 'Follow Up',
  conversation: 'Verified',
  not_interest: 'Not Interested',
  not_interested: 'Not Interested',
  do_not_call: 'Do Not Call',
  no_contact: 'No Contact',
  no_answer: 'No Answer',
  nurture: 'Nurture',
  alert: 'Alert',
  inbound: 'Inbound',
  active: 'Active',
  incomplete: 'Incomplete',
  voicemail: 'Voicemail',
  unclear: 'Unclear',
};

// Human-readable label for a call outcome. Stored values are messy (Title_Case,
// lowercase, spaces, underscores), so normalize to a key first, then map known
// outcomes; anything unlisted falls back to Title Case with spaces so a raw
// value like "no_answer" still reads as "No Answer".
export const formatOutcome = (outcome: string): string => {
  const key = (outcome ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return (
    OUTCOME_LABELS[key] ||
    key
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
};

// Plain-language description per outcome, shown in the outcome badge hover card
// and the Edit Call Analysis dropdown. Keyed by the normalized outcome value;
// not_interest/not_interested both map to the same copy.
export const OUTCOME_DESCRIPTIONS: Record<string, string> = {
  conversation: 'We confirmed a real person and had a conversation with them.',
  nurture: 'They have a real estate need and want us to keep following up.',
  follow_up: "They're interested but couldn't talk right now OR requested a follow up later.",
  not_interest:
    'They have no real estate need right now (side note: always needs additional follow up until they tell us to stop OR are ready to move forward).',
  not_interested:
    'They have no real estate need right now (side note: always needs additional follow up until they tell us to stop OR are ready to move forward).',
  do_not_call: 'They asked us not to call them again.',
  alert:
    "They mentioned an agent or recently bought/sold a property, may not need our services right now (side note: used even if a lead just mentioned the word 'agent', as in 'my friend is also an agent').",
  no_answer: 'The call rang but no one picked up.',
  voicemail: 'The call went to voicemail.',
  no_contact: 'We tried all our calls but never reached them.',
  inbound: 'They called us.',
  incomplete: 'The call failed due to an invalid phone number, dropped call or connection issue.',
};

export const getOutcomeDescription = (outcome: string | null | undefined): string | undefined => {
  const key = (outcome ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  return OUTCOME_DESCRIPTIONS[key];
};

export type FlowStatus = 'Draft' | 'Active' | 'Paused' | 'Scheduled' | 'Finished' | string;

export const getFlowStatusStyle = (status: FlowStatus): BadgeStyle => {
  switch (status.toLowerCase()) {
    case 'draft':
      return { background: 'badge-yellow', text: '' };
    case 'active':
      return { background: 'badge-green', text: '' };
    case 'paused':
      return { background: 'badge-orange', text: '' };
    case 'scheduled':
      return { background: 'badge-blue', text: '' };
    case 'finished':
      return { background: 'badge-purple', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export type FlowSubType = 'giveaway' | 'event' | 'quarter' | string;

export const getFlowSubTypeStyle = (subType: FlowSubType): BadgeStyle => {
  switch (subType.toLowerCase()) {
    case 'giveaway':
      return { background: 'badge-blue', text: '' };
    case 'event':
      return { background: 'badge-green', text: '' };
    case 'quarter':
      return { background: 'badge-purple', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export const getFlowTypeStyle = (flowType: FlowType): BadgeStyle => {
  switch (flowType.toLowerCase()) {
    case 'engage':
      return { background: 'badge-blue', text: '' };
    case 'convert':
      return { background: 'badge-purple', text: '' };
    case 'revive':
      return { background: 'badge-green', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export const getIntentStyle = (intent: IntentType): BadgeStyle => {
  switch (intent) {
    case 'Buy':
    case 'Buyer':
      return { background: 'badge-green', text: '' };
    case 'Sell':
    case 'Seller':
      return { background: 'badge-blue', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export const getIDXTypeStyle = (type: IDXType): BadgeStyle => {
  switch (type) {
    case 'View':
      return { background: 'badge-purple', text: '' };
    case 'Favorite':
      return { background: 'badge-orange', text: '' };
    case 'Search':
      return { background: 'badge-red', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export const getLeadSourceStyle = (source: LeadSourceType): BadgeStyle => {
  switch (source) {
    case 'Email':
      return { background: 'badge-blue', text: '' };
    case 'PPC':
      return { background: 'badge-purple', text: '' };
    case 'Organic':
      return { background: 'badge-green', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

export type FollowUpChannelType = 'call' | 'email' | 'sms';

export const getFollowUpChannelStyle = (channel: FollowUpChannelType): BadgeStyle => {
  switch (channel) {
    case 'call':
      return { background: 'badge-blue', text: '' };
    case 'email':
      return { background: 'badge-purple', text: '' };
    case 'sms':
      return { background: 'badge-green', text: '' };
    default:
      return { background: 'badge-gray', text: '' };
  }
};

/**
 * Get badge style for web activity event types
 * @param eventType - The event type from web activity
 * @param eventCategory - Optional category for property_view events (Property or Valuation)
 * @returns The badge style object
 */
export const getWebActivityEventTypeStyle = (
  eventType: WebActivityEventType,
  eventCategory?: string
): BadgeStyle => {
  // Special handling for property_view events with different categories
  if (eventType.toLowerCase() === 'property_view' && eventCategory) {
    if (eventCategory === 'Property') {
      return { background: 'web-evt-property-buyer', text: '' };
    } else if (eventCategory === 'Valuation') {
      return { background: 'web-evt-property-seller', text: '' };
    }
  }

  // Default handling for other event types
  switch (eventType.toLowerCase()) {
    case 'page_view':
      return { background: 'web-evt-pageview', text: '' };
    case 'click':
      return { background: 'web-evt-click', text: '' };
    case 'search':
      return { background: 'web-evt-search', text: '' };
    case 'property_view':
      return { background: 'web-evt-property', text: '' };
    case 'property_saved':
      return { background: 'web-evt-saved', text: '' };
    case 'lead_capture':
      return { background: 'web-evt-lead', text: '' };
    case 'form_submission':
      return { background: 'web-evt-form', text: '' };
    default:
      return { background: 'web-evt-default', text: '' };
  }
};

/**
 * Get badge style for an email lifecycle outcome.
 * Maps the message_status / sendgrid event names to the email-evt-* CSS classes.
 * Returns null when the value isn't a recognised lifecycle state (caller can skip the badge).
 *
 * The mapping deliberately distinguishes:
 *   - "sent"/"outbound"  — neutral; we attempted the send but haven't confirmed arrival
 *   - "delivered"        — neutral-positive; recipient mail server accepted it
 *   - "opened"/"clicked" — positive engagement (the only "good" outcomes)
 *   - all error states   — red, so problems are visible at a glance
 */
export const getEmailOutcomeStyle = (outcome?: string | null): BadgeStyle | null => {
  if (!outcome) return null;
  switch (outcome.toLowerCase()) {
    // Outbound: handed off to provider, not yet confirmed at recipient
    case 'sent':
    case 'outbound':
    case 'queued':
    case 'processed':
      return { background: 'email-evt-sent', text: '' };
    // Confirmed at recipient mail server
    case 'delivered':
      return { background: 'email-evt-delivered', text: '' };
    // Engagement
    case 'open':
    case 'opened':
      return { background: 'email-evt-opened', text: '' };
    case 'click':
    case 'clicked':
      return { background: 'email-evt-clicked', text: '' };
    // Errors — all red so problems pop
    case 'bounce':
    case 'bounced':
    case 'failed':
    case 'dropped':
    case 'blocked':
    case 'spamreport':
      return { background: 'email-evt-bounced', text: '' };
    // Transient retry — amber
    case 'deferred':
      return { background: 'email-evt-unsubscribed', text: '' };
    case 'unsubscribe':
    case 'unsubscribed':
      return { background: 'email-evt-unsubscribed', text: '' };
    case 'received':
    case 'inbound':
      return { background: 'email-evt-default', text: '' };
    default:
      return null;
  }
};

/** Format an email lifecycle outcome into a human label (e.g. "opened" → "Opened"). */
export const formatEmailOutcome = (outcome: string): string => {
  switch (outcome.toLowerCase()) {
    case 'sent':
    case 'outbound':
      return 'Sent';
    case 'queued':
      return 'Queued';
    case 'processed':
      return 'Processed';
    case 'delivered':
      return 'Delivered';
    case 'open':
    case 'opened':
      return 'Opened';
    case 'click':
    case 'clicked':
      return 'Clicked';
    case 'bounce':
    case 'bounced':
      return 'Bounced';
    case 'failed':
      return 'Failed';
    case 'dropped':
      return 'Dropped';
    case 'blocked':
      return 'Blocked';
    case 'deferred':
      return 'Deferred';
    case 'spamreport':
      return 'Spam Report';
    case 'unsubscribe':
    case 'unsubscribed':
      return 'Unsubscribed';
    case 'received':
    case 'inbound':
      return 'Received';
    default:
      return outcome.charAt(0).toUpperCase() + outcome.slice(1).toLowerCase();
  }
};
